import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Activity, RefreshCw, Zap } from "lucide-react";
import API_BASE from "../../config/api";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  width: 100%;
  min-width: 0;
`;

const SectionCard = styled.div`
  background: #111827;
  border: 1px solid #1f293d;
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);

  @media (max-width: 640px) {
    padding: 1rem 0.85rem;
    border-radius: 10px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
  gap: 0.75rem;

  @media (max-width: 640px) {
    gap: 0.5rem;
    margin-bottom: 1rem;
  }
`;

const Title = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #f3f4f6;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin: 0;

  @media (max-width: 480px) {
    font-size: 0.95rem;
  }

  span.badge {
    background: rgba(245, 158, 11, 0.15);
    color: #f59e0b;
    border: 1px solid rgba(245, 158, 11, 0.3);
    font-size: 0.72rem;
    padding: 0.15rem 0.55rem;
    border-radius: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const MetaInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.82rem;
  color: #9ca3af;
  flex-wrap: wrap;
`;

const PulseDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #10b981;
  box-shadow: 0 0 10px #10b981;
  animation: pulse 1.8s infinite;

  @keyframes pulse {
    0% { transform: scale(0.95); opacity: 0.8; }
    50% { transform: scale(1.3); opacity: 1; }
    100% { transform: scale(0.95); opacity: 0.8; }
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const RateItem = styled.div`
  background: #161f30;
  border: 1px solid #1f293d;
  border-radius: 10px;
  padding: 1rem 1.15rem;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-width: 0;
  overflow: hidden;

  &:hover {
    border-color: rgba(245, 158, 11, 0.4);
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.3);
  }

  @media (max-width: 640px) {
    padding: 0.85rem;
  }
`;

const MetalName = styled.div`
  font-weight: 600;
  font-size: 0.95rem;
  color: #f9fafb;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  overflow: hidden;

  span:first-child {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const PriceRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
`;

const PriceBox = styled.div`
  background: #0f1523;
  border-radius: 8px;
  padding: 0.55rem 0.65rem;
  border: 1px solid ${(props) => (props.$type === "buy" ? "rgba(16, 185, 129, 0.2)" : "rgba(239, 68, 68, 0.2)")};
  min-width: 0;
  overflow: hidden;

  .label {
    font-size: 0.68rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${(props) => (props.$type === "buy" ? "#10b981" : "#ef4444")};
    margin-bottom: 0.2rem;
    white-space: nowrap;
  }

  .value {
    font-size: clamp(0.9rem, 3.5vw, 1.05rem);
    font-weight: 700;
    color: #f3f4f6;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const RefreshBtn = styled.button`
  background: #1f293d;
  color: #d1d5db;
  border: 1px solid #374151;
  border-radius: 8px;
  padding: 0.4rem 0.8rem;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #374151;
    color: #fff;
  }

  &.spinning svg {
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    100% { transform: rotate(360deg); }
  }
`;

const AdminLiveRates = () => {
  const [rates, setRates] = useState({ custom: [], global: [], timestamp: null });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchRates = async () => {
    try {
      setRefreshing(true);
      const res = await fetch(`${API_BASE}/api/rates`);
      const data = await res.json();
      if (data && (data.custom || data.global)) {
        setRates(data);
      }
    } catch (err) {
      console.error("Failed to fetch live rates:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchRates();
    const interval = setInterval(fetchRates, 10000); // Poll every 10s
    return () => clearInterval(interval);
  }, []);

  return (
    <Container>
      {/* 🪙 Custom Rates */}
      <SectionCard>
        <CardHeader>
          <Title>
            <Zap size={18} color="#f59e0b" />
            GJ Bullion Spot Rates
            <span className="badge">Custom Terminal</span>
          </Title>
          <MetaInfo>
            <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
              <PulseDot /> Live Feed
            </div>
            {rates.timestamp && (
              <span>Updated: {new Date(rates.timestamp).toLocaleTimeString()}</span>
            )}
            <RefreshBtn onClick={fetchRates} className={refreshing ? "spinning" : ""}>
              <RefreshCw size={13} />
              Refresh
            </RefreshBtn>
          </MetaInfo>
        </CardHeader>

        {loading ? (
          <p style={{ color: "#9ca3af", fontSize: "0.9rem" }}>Loading live rates...</p>
        ) : rates.custom && rates.custom.length > 0 ? (
          <Grid>
            {rates.custom.map((item, idx) => (
              <RateItem key={idx}>
                <MetalName>
                  <span>{item.name}</span>
                  <span style={{ fontSize: "0.75rem", color: "#9ca3af", fontWeight: 400 }}>
                    {item.name.toLowerCase().includes("gold") ? "per 10g" : "per 1kg"}
                  </span>
                </MetalName>
                <PriceRow>
                  <PriceBox $type="buy">
                    <div className="label">BUY RATE</div>
                    <div className="value">
                      ₹{item.buy ? Number(item.buy).toLocaleString("en-IN") : "-"}
                    </div>
                  </PriceBox>
                  <PriceBox $type="sell">
                    <div className="label">SELL RATE</div>
                    <div className="value">
                      ₹{item.sell ? Number(item.sell).toLocaleString("en-IN") : "-"}
                    </div>
                  </PriceBox>
                </PriceRow>
              </RateItem>
            ))}
          </Grid>
        ) : (
          <p style={{ color: "#9ca3af", fontSize: "0.9rem" }}>No custom rates available at this moment.</p>
        )}
      </SectionCard>

      {/* 🌐 Global Rates */}
      {rates.global && rates.global.length > 0 && (
        <SectionCard>
          <CardHeader>
            <Title>
              <Activity size={18} color="#38bdf8" />
              Global Market Benchmarks
              <span className="badge" style={{ background: "rgba(56, 189, 248, 0.15)", color: "#38bdf8", borderColor: "rgba(56, 189, 248, 0.3)" }}>
                International / MCX
              </span>
            </Title>
          </CardHeader>

          <Grid>
            {rates.global.map((item, idx) => (
              <RateItem key={idx}>
                <MetalName>
                  <span>{item.name}</span>
                </MetalName>
                <PriceRow>
                  <PriceBox $type="buy">
                    <div className="label">BID</div>
                    <div className="value">
                      {item.bid ? Number(item.bid).toLocaleString("en-IN") : "-"}
                    </div>
                  </PriceBox>
                  <PriceBox $type="sell">
                    <div className="label">ASK</div>
                    <div className="value">
                      {item.ask ? Number(item.ask).toLocaleString("en-IN") : "-"}
                    </div>
                  </PriceBox>
                </PriceRow>
              </RateItem>
            ))}
          </Grid>
        </SectionCard>
      )}
    </Container>
  );
};

export default AdminLiveRates;
